import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import type { CalendarItem, PlatformId } from '../../shared/types';
import { isPlatformId } from '../../shared/types';
import { PLATFORM_LIST } from '../../shared/platforms';
import { PageHeader } from '../components/layout';
import { Alert, Button, EmptyState, Select, Skeleton, Tabs, TabsList, TabsTrigger } from '../components/ui';
import { CalendarGrid } from '../components/calendar/CalendarGrid';
import { WeekView } from '../components/calendar/WeekView';
import { PostDetailDrawer } from '../components/posts/PostDetailDrawer';
import {
  addDaysToCell,
  addMonths,
  buildMonthGrid,
  dayKey,
  MONTH_LABELS,
  startOfWeekMonday,
} from '../components/calendar/calendarDate';
import type { DayCell } from '../components/calendar/calendarDate';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { fromLocalInputValue, toLocalInputValue } from '../lib/format';
import { useWorkspaceStore, useWorkspaceTimezone } from '../stores/workspace';

type ViewMode = 'month' | 'week';

function todayCell(timezone: string): DayCell {
  const local = toLocalInputValue(Date.now(), timezone);
  const [year, month, day] = local.slice(0, 10).split('-').map(Number) as [number, number, number];
  return { key: local.slice(0, 10), year, month, day };
}

function monthCell({ year, month }: { year: number; month: number }): DayCell {
  return { year, month, day: 1, key: dayKey(year, month, 1) };
}

const PLATFORM_OPTIONS = [
  { value: '', label: 'All platforms' },
  ...PLATFORM_LIST.filter((spec) => !spec.comingSoon).map((spec) => ({
    value: spec.id,
    label: spec.shortName,
  })),
];

/**
 * Calendar — owned by WEB-3. Month and week views of scheduled/published
 * platform posts, colored by platform brand, with click-through detail and an
 * empty-day add affordance that deep-links to the composer.
 */
export function CalendarPage() {
  const navigate = useNavigate();
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const timezone = useWorkspaceTimezone();

  const [view, setView] = useState<ViewMode>('month');
  const [anchor, setAnchor] = useState<DayCell>(() => todayCell(timezone));
  const [platform, setPlatform] = useState<PlatformId | ''>('');
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);

  const today = useMemo(() => todayCell(timezone), [timezone]);

  const weekStart = useMemo(
    () => startOfWeekMonday(anchor.year, anchor.month, anchor.day),
    [anchor.year, anchor.month, anchor.day],
  );

  // The visible range — a full 6-week grid for month view, 7 days for week view.
  const rangeCells = useMemo(() => {
    if (view === 'week') {
      return Array.from({ length: 7 }, (_, index) => addDaysToCell(weekStart.year, weekStart.month, weekStart.day, index));
    }
    return buildMonthGrid(anchor.year, anchor.month);
  }, [view, anchor.year, anchor.month, weekStart]);

  const rangeFromIso = useMemo(() => {
    const first = rangeCells[0];
    return fromLocalInputValue(`${first.key}T00:00`, timezone) ?? new Date().toISOString();
  }, [rangeCells, timezone]);

  const rangeToIso = useMemo(() => {
    const last = rangeCells[rangeCells.length - 1];
    const nextDay = addDaysToCell(last.year, last.month, last.day, 1);
    return fromLocalInputValue(`${nextDay.key}T00:00`, timezone) ?? new Date().toISOString();
  }, [rangeCells, timezone]);

  const query = useQuery({
    queryKey: qk.calendar(workspaceId, rangeFromIso, rangeToIso),
    queryFn: () => api.calendar.range({ from: rangeFromIso, to: rangeToIso, platform: platform || undefined }),
    enabled: Boolean(workspaceId),
    placeholderData: (previous) => previous,
  });

  const items: CalendarItem[] = query.data?.items ?? [];

  const goToday = () => setAnchor(today);
  const goPrev = () =>
    setAnchor((current) =>
      view === 'week'
        ? addDaysToCell(current.year, current.month, current.day, -7)
        : monthCell(addMonths(current.year, current.month, -1)),
    );
  const goNext = () =>
    setAnchor((current) =>
      view === 'week'
        ? addDaysToCell(current.year, current.month, current.day, 7)
        : monthCell(addMonths(current.year, current.month, 1)),
    );

  const toggleExpand = (key: string) =>
    setExpandedDays((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const onAddDay = (key: string) => {
    const prefillIso = fromLocalInputValue(`${key}T09:00`, timezone);
    navigate(`/composer${prefillIso ? `?date=${encodeURIComponent(prefillIso)}` : ''}`, {
      state: prefillIso ? { prefillScheduledAt: prefillIso } : undefined,
    });
  };

  const rangeLabel =
    view === 'month'
      ? `${MONTH_LABELS[anchor.month - 1]} ${anchor.year}`
      : (() => {
          const end = addDaysToCell(weekStart.year, weekStart.month, weekStart.day, 6);
          const sameMonth = weekStart.month === end.month;
          return sameMonth
            ? `${MONTH_LABELS[weekStart.month - 1]} ${weekStart.day}–${end.day}, ${weekStart.year}`
            : `${MONTH_LABELS[weekStart.month - 1].slice(0, 3)} ${weekStart.day} – ${MONTH_LABELS[end.month - 1].slice(0, 3)} ${end.day}, ${end.year}`;
        })();

  return (
    <>
      <PageHeader
        title="Calendar"
        description="Scheduled and published posts across every platform, in your workspace timezone."
        actions={
          <>
            <Select
              aria-label="Filter by platform"
              selectSize="sm"
              wrapperClassName="w-auto min-w-32"
              options={PLATFORM_OPTIONS}
              value={platform}
              onChange={(event) => {
                const next = event.target.value;
                setPlatform(isPlatformId(next) ? next : '');
              }}
            />
            <Tabs value={view} onValueChange={(value) => setView(value as ViewMode)} variant="pill">
              <TabsList>
                <TabsTrigger value="month">Month</TabsTrigger>
                <TabsTrigger value="week">Week</TabsTrigger>
              </TabsList>
            </Tabs>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="icon-sm" aria-label="Previous" onClick={goPrev}>
            <ChevronLeft className="size-4" />
          </Button>
          <p className="min-w-40 text-sm font-medium text-fg">{rangeLabel}</p>
          <Button variant="ghost" size="icon-sm" aria-label="Next" onClick={goNext}>
            <ChevronRight className="size-4" />
          </Button>
          <Button variant="secondary" size="sm" onClick={goToday}>
            Today
          </Button>
          {query.isFetching && !query.isLoading && (
            <span className="text-xs text-subtle">Refreshing…</span>
          )}
        </div>
      </PageHeader>

      {query.isError ? (
        <Alert tone="danger" title="Could not load the calendar">
          {query.error instanceof ApiError ? query.error.message : 'Try again in a moment.'}
        </Alert>
      ) : query.isLoading ? (
        <Skeleton className="h-[32rem] w-full rounded-xl" />
      ) : (
        <div className="space-y-4">
          {view === 'month' ? (
            <CalendarGrid
              year={anchor.year}
              month={anchor.month}
              items={items}
              timezone={timezone}
              expandedDays={expandedDays}
              onToggleExpand={toggleExpand}
              onSelectItem={(item) => setSelectedPostId(item.postId)}
              onAddDay={onAddDay}
            />
          ) : (
            <WeekView
              weekStart={weekStart}
              items={items}
              timezone={timezone}
              onSelectItem={(item) => setSelectedPostId(item.postId)}
              onAddDay={onAddDay}
            />
          )}
          {items.length === 0 && (
            <EmptyState
              variant="plain"
              icon={<CalendarDays className="size-5" />}
              title="Nothing on the calendar yet"
              description="Schedule a post from the composer, or use the + on any day above."
              action={<Button onClick={() => navigate('/composer')}>Schedule a post</Button>}
            />
          )}
        </div>
      )}

      <PostDetailDrawer postId={selectedPostId} onClose={() => setSelectedPostId(null)} />
    </>
  );
}

export default CalendarPage;
